import Geolocation from '@react-native-community/geolocation';
import { PermissionsAndroid, Platform } from 'react-native';

/**
 * Where the phone thinks it is, in the seconds an emergency allows.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS RATHER THAN REUSING LocationScreen'S FIX
 * ---------------------------------------------------------------------------
 * `LocationScreen` already takes a two-stage fix - precise, then coarse - and
 * it is the right shape. Its timeouts are not: 12 seconds precise, 6 coarse,
 * `maximumAge: 0` to force a fresh GPS lock. That is correct when somebody is
 * dropping a pin to book a job and wrong when somebody is frightened, because
 * it can hold the alarm for eighteen seconds before giving up.
 *
 * Here the priorities invert:
 *
 *   - A CACHED FIX IS WELCOME. `maximumAge` is generous. A position from two
 *     minutes ago is a building, and a building is what ops needs. The pin
 *     precision that matters for a booking does not matter here.
 *   - THE DEADLINE IS HARD. `locate()` resolves within `FIX_DEADLINE_MS`
 *     whatever happens, with null if nothing arrived. The alarm goes with
 *     whatever is in hand.
 *   - IT NEVER REJECTS. No permission, services off, indoors - all resolve to
 *     null. A missing fix must cost the alarm its coordinates, never its
 *     existence.
 *
 * ---------------------------------------------------------------------------
 * PERMISSION IS ASKED FOR, NOT WAITED ON
 * ---------------------------------------------------------------------------
 * If location was never granted, the request is fired and the fix is attempted
 * anyway. A customer who has just held the alarm button must not be met with a
 * system dialog standing between them and help - if they grant it, later
 * presses carry a position; if they do not, the address snapshot is what ops
 * gets, which is what happened before this file existed.
 */

/**
 * The whole budget for getting a position, in ms.
 *
 * 3.5 seconds. Long enough for a cached or network fix on a normal phone,
 * short enough that nobody watching the screen registers a delay. A GPS lock
 * from cold takes longer than this and is not worth waiting for.
 */
export const FIX_DEADLINE_MS = 3500;

/** The precise attempt's own budget, inside the deadline above. */
const PRECISE_MS = 2500;

/**
 * How old a cached position may be and still be used, in ms.
 *
 * Two minutes. `LocationScreen` deliberately passes 0 here to stop a stale
 * coarse fix from another app landing the pin in the wrong suburb. That risk
 * is real and is accepted here: a two-minute-old position is still the right
 * building, and the alternative is no position at all. `locationAt` travels
 * with the fix so ops can see how old it is and judge for themselves.
 */
const MAX_AGE_MS = 120000;

export type Fix = {
  latitude: number;
  longitude: number;
  /** Metres of uncertainty, when the platform reports it. */
  accuracy?: number;
  /** When the fix was taken - ISO, so ops can see how stale it is. */
  at: string;
};

/**
 * Ask for location if we do not have it. Never blocks the caller for long and
 * never throws.
 *
 * On iOS `requestAuthorization` is fire-and-forget by design; the fix attempt
 * that follows is what actually triggers the prompt.
 */
async function ensurePermission(): Promise<void> {
  try {
    if (Platform.OS !== 'android') {
      Geolocation.requestAuthorization();
      return;
    }

    const already = await PermissionsAndroid.check(
      PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION,
    );
    if (already) {
      return;
    }
    await PermissionsAndroid.request(
      PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION,
    );
  } catch {
    // Denied, or the dialog could not be shown. The fix attempt below will
    // simply fail, which is already a handled outcome.
  }
}

/** One `getCurrentPosition` as a promise that resolves null instead of throwing. */
const attempt = (options: {
  enableHighAccuracy: boolean;
  timeout: number;
  maximumAge: number;
}): Promise<Fix | null> =>
  new Promise(resolve => {
    let settled = false;
    const done = (value: Fix | null) => {
      if (!settled) {
        settled = true;
        resolve(value);
      }
    };

    try {
      Geolocation.getCurrentPosition(
        position =>
          done({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy:
              typeof position.coords.accuracy === 'number'
                ? position.coords.accuracy
                : undefined,
            /*
             * The platform's own timestamp where there is one - a cached fix
             * is older than "now" and saying otherwise would misrepresent how
             * fresh it is to the person deciding whether to trust it.
             */
            at: new Date(position.timestamp ?? Date.now()).toISOString(),
          }),
        () => done(null),
        options,
      );
    } catch {
      done(null);
    }
  });

/**
 * The best position obtainable inside the deadline, or null.
 *
 * Precise first, falling back to coarse - the same two-stage shape as
 * `LocationScreen`, on an emergency's clock. The outer race is what guarantees
 * the deadline even if the platform never calls either callback, which is a
 * real failure mode on Android when location services are toggled mid-call.
 */
export async function locate(): Promise<Fix | null> {
  await ensurePermission();

  const staged = (async (): Promise<Fix | null> => {
    const precise = await attempt({
      enableHighAccuracy: true,
      timeout: PRECISE_MS,
      maximumAge: MAX_AGE_MS,
    });
    if (precise) {
      return precise;
    }
    return attempt({
      enableHighAccuracy: false,
      timeout: FIX_DEADLINE_MS - PRECISE_MS,
      maximumAge: MAX_AGE_MS,
    });
  })();

  const deadline = new Promise<null>(resolve => {
    setTimeout(() => resolve(null), FIX_DEADLINE_MS);
  });

  return Promise.race([staged, deadline]);
}
